// safety/incidents-and-injuries/components/tabs/OverviewTab.tsx
import React from 'react';
import { Check, Zap, Hand } from 'lucide-react';
import { InfoCell, SectionTitle } from '../shared';
import { fmtDate, capacityColor, mergedWorkflow } from '../../utils/incidents-utils';
import type { Incident } from '../../../../../types/incident.types';

export const OverviewTab: React.FC<{ incident: Incident; onCompleteStep: (step: number) => void }> = ({
    incident: r,
    onCompleteStep,
}) => {
    const steps = mergedWorkflow(r);
    return (
        <>
            <div className="grid grid-cols-2 gap-3 mb-4">
                <InfoCell label="Employee Name">{r.employeeName}</InfoCell>
                <InfoCell label="Employer Entity">{r.entityName}</InfoCell>
                <InfoCell label="Project / Site">{r.project}</InfoCell>
                <InfoCell label="Supervisor">{r.supervisor}</InfoCell>
                <InfoCell label="Incident Date">{fmtDate(r.date)}</InfoCell>
                <InfoCell label="Incident Time">{r.time}</InfoCell>
                <InfoCell label="RTW Coordinator">{r.rtwCoordinator}</InfoCell>
                <InfoCell label="Current Capacity">
                    <span className={capacityColor(r.capacity)}>{r.capacity || '—'}</span>
                </InfoCell>
            </div>

            <div className="rounded-lg border border-border p-4 mb-5">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Injury Description</div>
                <p className="text-sm leading-relaxed">{r.description || '—'}</p>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
                <InfoCell label="Cause of Injury">{r.cause}</InfoCell>
                <InfoCell label="Body Part Injured">{r.bodyPart}</InfoCell>
                <InfoCell label="Nature of Injury">{r.nature}</InfoCell>
                <InfoCell label="First Aid Applied">{r.firstAidApplied ? 'Yes' : 'No'}</InfoCell>
                <InfoCell label="Hospitalised">{r.hospitalised ? 'Yes' : 'No'}</InfoCell>
                <InfoCell label="Fit to Return">{r.fitToReturn === null || r.fitToReturn === undefined ? '—' : r.fitToReturn ? 'Yes' : 'No'}</InfoCell>
            </div>

            <SectionTitle
                title="Return To Work Workflow"
                right={
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                            <Zap size={12} />
                            Auto-triggered
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Hand size={12} />
                            Manual action
                        </span>
                    </div>
                }
            />
            <div className="space-y-2">
                {steps.map((s) => (
                    <div
                        key={s.step}
                        className={`flex items-center gap-3 rounded-lg border px-3.5 py-3 ${
                            s.done ? 'border-emerald-200 bg-emerald-50/60' : 'border-border'
                        }`}
                    >
                        <div
                            className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                                s.done ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}
                        >
                            {s.done ? <Check size={14} /> : s.step}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium ${s.done ? 'text-emerald-700' : ''}`}>
                                {s.label} {s.auto ? <Zap size={12} className="inline text-muted-foreground" /> : <Hand size={12} className="inline text-muted-foreground" />}
                            </div>
                            <div className="text-xs text-muted-foreground">{s.done ? s.who || '—' : 'Pending'}</div>
                        </div>
                        {s.done ? (
                            <span className="text-xs text-emerald-600 font-medium whitespace-nowrap">Completed {fmtDate(s.date)}</span>
                        ) : s.auto ? (
                            <span className="text-xs text-muted-foreground whitespace-nowrap">Auto-completes</span>
                        ) : (
                            <button
                                onClick={() => onCompleteStep(s.step)}
                                className="text-xs font-medium h-8 px-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap"
                            >
                                Mark Complete
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </>
    );
};
